<template>
    <div class="text-blocks-wrapper" :class="{ 'apply-ellipsis': applyEllipsis === true }">
        <!-- Label -->
        <label-generic
        v-if="params.label"
        :text="params.label"
        :mandatory="params.mandatory"
        :errorState="params.errorState"/>
        <!-- Editable Content -->
        <div class="content-wrapper" @click="showEditModal = true">
            <!-- Content -->
            <div
            class="editable-content"
            :class="{ 'apply-ellipsis': applyEllipsis === true }"
            v-mask="params.mask">
                {{ params.content }}
            </div>
            <!-- Edit Icon -->
            <edit-icon/>
        </div>
        <!-- Edit Modal -->
        <edit-input-modal
        v-if="showEditModal"
        :focusInput="focusInput"
        :modalParams="params"
        :requestPayload="requestPayload"
        @hideMe="showEditModal = false"
        @requestSuccessful="[emit('requestSuccessful', $event.data), showToastHandler($event)]"
        @input="emit('updateContent', $event)"/>
        <!-- Feedback Toast -->
        <toast-generic
        v-if="showToast"
        :feedbackText="feedbackToastParams.text"
        :toastType="feedbackToastParams.toastType"
        @hideMe="showToast = false"/>
    </div>
</template>

<script setup>
import LabelGeneric from '@/components/generic/labels/Generic'
import EditIcon from '@/assets/icons/pencil.svg?inline'
import EditInputModal from '@/components/generic/modals/centered/EditInputModal'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref } from 'vue'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['updateContent', 'requestSuccessful'])

/** props received
@params { Object } with:
	- label { String }
    - title { String } - modal title
    - content { String, Number }
	- mandatory { Boolean }
	- errorState { Boolean }
    - maxLength { String, Number}
    - editAction  { String }
@applyEllipsis { Boolean } - instead of the field growing vertically with the content, we can apply ellipsis
@mask { Object } - mask to apply in our content
@requestPayload { Object } - Request Payload for edit input modal
@focusInput {Boolean} - prop to trigger input focus
*/

const props = defineProps({
    params: Object,
    applyEllipsis: Boolean,
    mask: Object,
    requestPayload: Object,
    focusInput: Boolean
})

const showEditModal = ref(false)

/*************************************************
*                                                *
*               Feedback Toast Logic             *
*                                                *
*************************************************/
const showToast = ref(false)
const feedbackToastParams = ref({ text: '', toastType: 'info' })

const showToastHandler = response => {
    feedbackToastParams.value = {
        text: response.message || t('genericErrorMessage'),
        toastType: response.errorLevel.toLowerCase() === 'error' ? 'negative' : response.errorLevel.toLowerCase()
    }
    showToast.value = true
}

</script>

<style lang="scss" scoped>
.text-blocks-wrapper {
	width: 100%;
    max-width: 400px;
	height: 100%;

    position: relative;

    &.apply-ellipsis {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .content-wrapper {
        width: fit-content;
        max-width: 100%;

        display: flex;
        align-items: flex-end;

        margin-top: 4px;
        gap: 8px;

        cursor: pointer;

        .editable-content {
            @include H6-Regular;
            line-height: 18px;
            color: var(--secondary-dark-2);

            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
        }

        svg {
            width: 20px;
            height: 20px;

            flex-shrink: 0;
            cursor: pointer;

            :deep(.change-color-fill) {
                fill: var(--secondary-dark-2);
            }
        }
    }
}
</style>
